import React, { useEffect, useRef, useState } from "react";
import AiIcon from "../../assets/icons/Icon_Library/application icons/library/boomAI_icon.svg2";

interface SolutionAiSuggestionMarkerProps {
  suggestions?: any[];
  isLoading?: boolean;
  onSuggestionClick?: (suggestion: any) => void;
  onApplySuggestion?: (suggestion: any) => void;
  onRemoveSuggestion?: (suggestion: any) => void;
}

const stringifySuggestionValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) {
    return value.map(stringifySuggestionValue).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    return stringifySuggestionValue(
      value.Label || value.label || value.Name || value.name || value.Value || value.value
    );
  }

  return String(value);
};

const SolutionAiSuggestionMarker: React.FC<SolutionAiSuggestionMarkerProps> = ({
  suggestions = [],
  isLoading = false,
  onSuggestionClick,
  onApplySuggestion,
  onRemoveSuggestion,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openMarker = () => {
    clearCloseTimeout();
    setIsOpen(true);
  };

  const closeMarker = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimeoutRef.current = null;
    }, 150);
  };

  useEffect(() => clearCloseTimeout, []);

  if (!suggestions.length) return null;

  return (
    <div
      className="position-absolute"
      style={{ top: -10, right: -10, zIndex: 20 }}
      onMouseEnter={openMarker}
      onMouseLeave={closeMarker}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center position-relative bg-white rounded-circle shadow-sm border"
        role="button"
        tabIndex={0}
        aria-label={`${suggestions.length} AI suggestion${suggestions.length === 1 ? "" : "s"}`}
        style={{ width: 32, height: 32 }}
        onFocus={openMarker}
        onBlur={closeMarker}
      >
        <AiIcon width={24} className="" />
        <span className="badge bg-colored rounded-pill position-absolute top-0 start-100 translate-middle">
          {suggestions.length}
        </span>
      </span>

      {isOpen && (
        <div
          className="position-absolute bg-white border rounded shadow p-2"
          style={{ top: 36, right: 0, width: 340, maxWidth: "min(340px, 80vw)", zIndex: 30 }}
          onMouseEnter={openMarker}
          onMouseLeave={closeMarker}
        >
          <div className="d-flex flex-column gap-2">
            {suggestions.map((suggestion, index) => {
              const suggestedChange = suggestion.action?.change;
              const currentText = stringifySuggestionValue(suggestedChange?.from);
              const suggestionText = stringifySuggestionValue(suggestedChange?.to);

              return (
                <div
                  key={`${suggestion.title || "suggestion"}-${index}`}
                  className="border rounded p-2 bg-white"
                  role="button"
                  tabIndex={0}
                  style={{ cursor: "pointer" }}
                  onClick={() => onSuggestionClick?.(suggestion)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSuggestionClick?.(suggestion);
                    }
                  }}
                >
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div className="min-w-0">
                      {suggestion.stage && (
                        <div className="text-secondary text-small">{suggestion.stage}</div>
                      )}
                      {!currentText || !suggestionText ? (
                        <div className="fw-bold text-small">{suggestion.title}</div>
                      ) : null}
                    </div>
                    <div className="d-flex align-items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={isLoading}
                        onClick={(event) => {
                          event.stopPropagation();
                          onRemoveSuggestion?.(suggestion);
                        }}
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        disabled={isLoading}
                        onClick={(event) => {
                          event.stopPropagation();
                          onApplySuggestion?.(suggestion);
                        }}
                      >
                        Apply
                      </button>
                    </div>
                  </div>

                  {currentText && suggestionText ? (
                    <div className="d-flex flex-column gap-1 mt-1">
                      <div className="fw-bold text-small">{currentText}</div>
                      <div className="text-secondary text-small">=&gt;</div>
                      <div className="fw-bold text-small">{suggestionText}</div>
                    </div>
                  ) : null}
                  {suggestion.detail && (
                    <div className="text-secondary text-small mt-1">{suggestion.detail}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SolutionAiSuggestionMarker;
